/**
 * Affiliate (星推官) admin API
 *
 * Application review, affiliate management, and program config.
 * Endpoints are admin-only under the `/v1/admin` prefix (auto-added by apiClient).
 */

import { apiClient } from './client';

export type AffiliateApplicationStatus = 'pending' | 'approved' | 'rejected';

export interface AffiliateApplication {
  id: string;
  user_id: string;
  status: AffiliateApplicationStatus;
  note?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  created_at: string;
}

export interface AffiliateBalance {
  pending_cents: number;
  cleared_cents: number;
  withdrawable_cents: number;
  clawed_back_cents: number;
  negative_balance: boolean;
  currency: string;
}

export interface AffiliateAdminItem {
  id: string;
  user_id: string;
  code: string;
  commission_rate: number;
  enabled: boolean;
  valid_from?: string | null;
  valid_until?: string | null;
  created_at: string;
  balance: AffiliateBalance;
}

export interface AffiliateConfig {
  default_commission_rate: number;
  commission_hold_days: number;
}

export interface ApproveApplicationPayload {
  commission_rate?: number;
  valid_until?: string;
}

export interface RejectApplicationPayload {
  note?: string;
}

export interface UpdateAffiliatePayload {
  commission_rate?: number;
  valid_until?: string;
  enabled?: boolean;
}

export type PayoutStatus = 'requested' | 'approved' | 'paid' | 'rejected';

export interface PayoutItem {
  id: string;
  affiliate_id: string;
  amount_cents: number;
  currency: string;
  status: PayoutStatus;
  note?: string | null;
  requested_at: string;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  paid_at?: string | null;
}

export type PayoutAction = 'approve' | 'reject' | 'mark_paid';

export interface ReviewPayoutPayload {
  action: PayoutAction;
  note?: string;
}

export const affiliateApi = {
  async getApplications(status?: AffiliateApplicationStatus): Promise<AffiliateApplication[]> {
    const { data } = await apiClient.get<AffiliateApplication[]>('/affiliate/applications', {
      params: status ? { status } : undefined,
    });
    return data;
  },

  async approveApplication(
    id: string,
    payload: ApproveApplicationPayload,
  ): Promise<AffiliateAdminItem> {
    const { data } = await apiClient.post<AffiliateAdminItem>(
      `/affiliate/applications/${id}/approve`,
      payload,
    );
    return data;
  },

  async rejectApplication(id: string, payload: RejectApplicationPayload): Promise<void> {
    await apiClient.post(`/affiliate/applications/${id}/reject`, payload);
  },

  async getAffiliates(): Promise<AffiliateAdminItem[]> {
    const { data } = await apiClient.get<AffiliateAdminItem[]>('/affiliate/affiliates');
    return data;
  },

  async updateAffiliate(id: string, payload: UpdateAffiliatePayload): Promise<AffiliateAdminItem> {
    const { data } = await apiClient.put<AffiliateAdminItem>(`/affiliate/affiliates/${id}`, payload);
    return data;
  },

  async getConfig(): Promise<AffiliateConfig> {
    const { data } = await apiClient.get<AffiliateConfig>('/affiliate/config');
    return data;
  },

  async updateConfig(payload: AffiliateConfig): Promise<AffiliateConfig> {
    const { data } = await apiClient.put<AffiliateConfig>('/affiliate/config', payload);
    return data;
  },

  async listPayouts(status?: PayoutStatus): Promise<PayoutItem[]> {
    const { data } = await apiClient.get<PayoutItem[]>('/affiliate/payouts', {
      params: status ? { status } : undefined,
    });
    return data;
  },

  async reviewPayout(id: string, payload: ReviewPayoutPayload): Promise<PayoutItem> {
    const { data } = await apiClient.post<PayoutItem>(`/affiliate/payouts/${id}/review`, payload);
    return data;
  },
};
